import { citySearchTerms, stateSearchTerms } from "@/lib/format-location"
import { countrySearchTerms } from "@/lib/country-from-state"

/**
 * Lowercase and strip accents so "mexico" matches "México" and a shop stored
 * as "Ciudad de México" is reachable from a plain ASCII keyboard.
 */
export function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

/**
 * Splits a raw query into normalized words. Every word has to match for a
 * listing to survive, so "brooklyn barber" narrows to Brooklyn barbershops
 * instead of returning everything matching either word.
 */
export function tokenizeSearchQuery(query: string): string[] {
  return normalizeForSearch(query || "")
    .split(/\s+/)
    .filter(Boolean)
}

/** The subset of a listing that search reads. */
export type SearchableListing = {
  name?: string | null
  city?: string | null
  state?: string | null
  address?: string | null
  zip?: string | null
  description?: string | null
  shopType?: string | null
  features?: string[] | null
}

/**
 * Everything a listing can be found by, flattened into one normalized string.
 * Build once per listing so each keystroke is a substring scan rather than a
 * walk over every field.
 *
 * `typeLabel` is the already-translated shop-type label, passed in so this
 * stays free of React/i18n imports.
 */
export function buildSearchHaystack(listing: SearchableListing, typeLabel?: string): string {
  const parts: (string | null | undefined)[] = [
    listing.name,
    listing.address,
    listing.zip,
    listing.description,
    listing.shopType,
    typeLabel,
    ...citySearchTerms(listing.city || ""),
    ...stateSearchTerms(listing.state || ""),
    // Country, derived from the region. Nothing here knew what country a shop
    // was in, so "japan" found nothing for a Tokyo shop and "canada" nothing
    // for Abbotsford — "mexico" only worked by accident, because the CDMX city
    // alias happens to contain the word. People search by country before they
    // search by prefecture.
    ...countrySearchTerms(listing.state || ""),
    ...(listing.features || []),
  ]
  return normalizeForSearch(parts.filter(Boolean).join(" "))
}

/** True when every token appears somewhere in the prebuilt haystack. */
export function matchesSearchTokens(haystack: string, tokens: string[]): boolean {
  return tokens.every((token) => haystack.includes(token))
}
